import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import i18n from "@/lib/i18n";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary]", error, errorInfo);
    // Best-effort: persist the unminified error to admin_notifications for later inspection.
    try {
      void supabase.from("admin_notifications").insert({
        type: "error_log" as never,
        title: "Production error captured",
        message: `${error.message}\n\nStack:\n${error.stack ?? "(no stack)"}\n\nComponent:\n${errorInfo.componentStack ?? ""}\n\nURL: ${typeof window !== "undefined" ? window.location.href : ""}`,
        related_entity_type: "client",
        related_entity_id: "00000000-0000-0000-0000-000000000000",
        is_read: false,
      });
    } catch {
      /* ignore */
    }
  }

  reset = () => {
    this.setState({ error: null });
    window.location.assign("/");
  };

  render() {
    if (!this.state.error) return this.props.children;
    const t = (k: string, f: string) => {
      const v = i18n.t(k);
      return v === k ? f : v;
    };
    return (
      <section className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-lg rounded-2xl border border-border bg-card p-8 text-center shadow-soft animate-fade-in">
          <div className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-full bg-destructive/15 text-destructive">
            <AlertTriangle className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-black">{t("errorPage.title", "Something went wrong")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("errorPage.message", "An unexpected error occurred. Please try again or contact us.")}
          </p>
          <p className="mt-3 break-words rounded-md bg-muted/50 p-3 text-xs text-muted-foreground" dir="ltr">
            {this.state.error.message}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              onClick={this.reset}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-premium transition hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <RefreshCw className="h-4 w-4" /> {t("errorPage.retry", "Reload home")}
            </button>
            <a
              href="/contact"
              className="inline-flex items-center rounded-full border border-border px-5 py-2.5 text-sm font-bold transition hover:bg-secondary"
            >
              {t("errorPage.contact", "Contact support")}
            </a>
          </div>
        </div>
      </section>
    );
  }
}
